<template>
  <BaseFormLayout
    :title="`${editMode ? 'Edit' : 'Add'} ${companyModule.company}`"
    :nonFieldErrors="formErrors.non_field_errors"
    :isHeader="isHeader"
    :customHeight="!isHeader ? 'calc(100vh - 220px)' : 'calc(100vh - 130px)'"
  >
    <template #actions>
      <SecondaryButton
        id="companiesReset"
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
      </SecondaryButton>
      <PrimaryButton
        id="companiesSubmit"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
        @click="submitForm()"
        :disabled="!isValid"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="companyForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-card elevation="0" class="rounded-lg pa-4">
          <v-row class="ma-0">
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex flex-column justify-center align-center"
            >
              <ProfileUpload
                :defaultImage="defaultImage"
                v-model="profileImage"
                @UploadProfile="updateProfile"
              >
              </ProfileUpload>
              <span class="text-caption mt-7"
                >A logo size of 160 x 60 pixels is recommended</span
              >
            </v-col>
            <v-col cols="12" md="9" lg="9">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >{{ companyModule.company }} Information</span
                  >
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="name"
                    v-model="companyDetails.name"
                    :label="`${companyModule.company} Name`"
                    :required="true"
                    name="name"
                    :rules="[
                      (val) => {
                        return (
                          !(/[0-9]/gi.test(val) && !/[a-z]/gi.test(val)) ||
                          `${companyModule.company} name can not be just numbers!`
                        );
                      },
                      (v) =>
                        !/[^a-zA-Z0-9 _.-]/.test(v) ||
                        'Special characters are not allowed',
                    ]"
                    :error-messages="formErrors.name"
                    @input="delete formErrors.name"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                  lg="3"
                  v-if="
                    profileObject && profileObject.role != 'account_manager'
                  "
                >
                  <SelectField
                    id="account_manager"
                    label="Account Manager *"
                    :itemsList="accountManagerSelectList"
                    name="account_manager"
                    v-model.trim="companyDetails.account_manager"
                    :rules="[(val) => !!val || 'Account Manager is required']"
                    :error-messages="formErrors.account_manager"
                    @input="delete formErrors.account_manager"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="contact_number"
                    type="number"
                    v-model="companyDetails.contact_number"
                    label="Contact Number"
                    name="contact_number"
                    :required="true"
                    :rules="[
                      (val) =>
                        (val && val.length >= 9 && val.length <= 12) ||
                        'Contact number should be between 9 to 12 digits!',
                    ]"
                    :error-messages="formErrors.contact_number"
                    @input="delete formErrors.contact_number"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    v-model="companyDetails.address"
                    dense
                    label="Address"
                    name="address"
                    hide-details="auto"
                    id="addressField"
                    outlined
                    :required="true"
                    :error-messages="formErrors.address"
                    @input="delete formErrors.address"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <SelectField
                    v-model="companyDetails.theme"
                    :itemsList="themeList"
                    name="theme"
                    itemText="name"
                    itemValue="id"
                    :rules="[(val) => !!val || 'Theme is required']"
                    label="Select Theme *"
                    prepend-inner-icon="mdi-eye"
                    @change="themeChanged"
                  >
                    <template #prepend-inner>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-icon
                            class="pr-2"
                            v-on="on"
                            v-bind="attrs"
                            @click.stop="getThemePreview"
                            >mdi-eye</v-icon
                          >
                        </template>
                        <span>Theme Preview</span>
                      </v-tooltip>
                    </template>
                  </SelectField>
                </v-col>
                <v-col cols="12" md="4" lg="3" class="d-flex align-center">
                  <SelectField
                    id="subscription"
                    v-if="!editMode"
                    v-model="companyDetails.subscription"
                    name="subscription"
                    :itemsList="subscriptionList"
                    itemText="name"
                    itemValue="id"
                    :rules="[(val) => !!val || 'Subscription is required']"
                    :label="`Select ${subscriptionModule.subscription} *`"
                  />
                  <InputField
                    id="current_subscription"
                    v-else
                    label="Subscription"
                    name="current_subscription"
                    class="input-disabled"
                    disabled
                    v-model.trim="companyDetails.current_subscription"
                    :error-messages="formErrors.current_subscription"
                    @input="delete formErrors.current_subscription"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <SelectField
                    id="preferred_timezone"
                    v-model="companyDetails.preferred_timezone"
                    name="preferred_timezone"
                    :itemsList="timezoneList"
                    itemText="name"
                    itemValue="id"
                    :rules="[(val) => !!val || 'Timezone is required']"
                    label="Timezone *"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3">
                  <InputField
                    id="website"
                    label="Website"
                    name="website"
                    v-model.trim="companyDetails.website"
                    :error-messages="formErrors.website"
                    @input="delete formErrors.website"
                  />
                </v-col>
                <v-col cols="12" md="4" lg="3" class="d-flex align-center">
                  <SwitchField
                    id="is_active"
                    v-model="companyDetails.is_active"
                    name="is_active"
                    :label="`${
                      companyDetails.is_active ? 'Active' : 'Deactivated'
                    }`"
                    :error-messages="formErrors.is_active"
                    @change="delete formErrors.is_active"
                  />
                </v-col>
                <v-col cols="12" md="3" lg="3" class="d-flex align-center">
                  <SwitchField
                    id="email_notification"
                    v-model="companyDetails.email_notification"
                    name="email_notification"
                    label="Email Notification"
                    :error-messages="formErrors.email_notification"
                    @change="delete formErrors.email_notification"
                  />
                </v-col>
                <v-col cols="12" md="3" lg="3" class="d-flex align-center">
                  <SwitchField
                    id="geofence_required"
                    v-model="companyDetails.geofence_required"
                    name="geofence_required"
                    label="Geofence Required"
                    :error-messages="formErrors.geofence_required"
                    @change="delete formErrors.geofence_required"
                  />
                </v-col>
              </v-row>
              <v-row class="ma-0">
                <v-col cols="12" md="4" lg="6">
                  <v-form ref="emails" @submit.prevent="">
                    <InputField
                      id="emails"
                      v-model="supportEmail"
                      name="emails"
                      label="Support Email"
                      persistent-hint
                      :rules="[
                        (v) => !v || emailValidation(v) || 'Invalid email id',
                      ]"
                      hint="Enter Email & press enter to add it to the list"
                      :error-messages="formErrors.support_emails"
                      @keydown.enter="
                        addEmail();
                        delete formErrors.support_emails;
                      "
                    />
                  </v-form>
                  <div>
                    <v-chip
                      v-for="(elem, i) in companyDetails.support_emails"
                      :key="i"
                      class="ma-2"
                      color="primary"
                      close
                      @click:close="
                        companyDetails.support_emails.splice(i, 1);
                        delete formErrors.support_emails;
                      "
                    >
                      {{ elem }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" md="4" lg="6">
                  <TextAreaField
                    id="remarks"
                    v-model="companyDetails.remarks"
                    name="remarks"
                    label="Remarks"
                    :error-messages="formErrors.remarks"
                    @input="delete formErrors.remarks"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" class="d-flex align-center py-md-0">
              <span class="text-subtitle-2 font-weight-bold">
                Packaging Configuration
              </span>
            </v-col>
            <v-col cols="12" md="3" lg="3" class="d-flex align-center pb-md-5">
              <InputField
                id="packaging_type"
                label="Packaging Type"
                name="packaging_type"
                v-model.trim="companyDetails.packaging_type"
                :required="true"
                :error-messages="formErrors.packaging_type"
                @input="delete formErrors.packaging_type"
              />
            </v-col>
            <v-col cols="12" md="3" lg="3" class="d-flex align-center pb-md-5">
              <InputField
                type="number"
                id="packaging_capacity"
                step=".0004"
                label="Packaging Capacity"
                name="packaging_capacity"
                v-model="companyDetails.packaging_capacity"
                :required="true"
                suffix="CBM"
                :error-messages="formErrors.packaging_capacity"
                @input="delete formErrors.packaging_capacity"
              />
            </v-col>
            <v-col cols="12" md="3" lg="3" class="d-flex align-center pb-md-5">
              <InputField
                type="number"
                id="packaging_ratio"
                step=".0004"
                label="Packaging Fill Ratio"
                name="packaging_ratio"
                v-model="companyDetails.packaging_ratio"
                :required="true"
                :rules="[
                  (val) => val > 0 || 'Packaging fill ratio can not be 0',
                  (val) =>
                    val <= 100 ||
                    'Packaging fill ratio can not be more than 100',
                ]"
                suffix="%"
                :error-messages="formErrors.packaging_ratio"
                @input="delete formErrors.packaging_ratio"
              />
            </v-col>
            <v-col cols="12" class="d-flex align-center py-md-0">
              <span class="text-subtitle-2 font-weight-bold">
                {{ orderModule.order }} Configuration
              </span>
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.order_configuration.allow_auto_address_update
                "
                name="allow_auto_address_update"
                label="Allow auto address update"
                :error-messages="formErrors.allow_auto_address_update"
                @change="delete formErrors.allow_auto_address_update"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="companyDetails.order_configuration.has_items"
                name="has_items"
                label="Has Items"
                :disabled="editMode"
                :error-messages="formErrors.has_items"
                @change="delete formErrors.has_items"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.order_configuration.customer_barcode_scanning
                "
                name="customer_barcode_scanning"
                label="Customer Barcode Scanning"
                :error-messages="formErrors.customer_barcode_scanning"
                @change="delete formErrors.customer_barcode_scanning"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.order_configuration.warehouse_barcode_scanning
                "
                name="warehouse_barcode_scanning"
                label="Warehouse Barcode Scanning"
                :error-messages="formErrors.warehouse_barcode_scanning"
                @change="delete formErrors.warehouse_barcode_scanning"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="companyDetails.order_configuration.order_invoice"
                name="order_invoice"
                label="Order Invoice"
                :error-messages="formErrors.order_invoice"
                @change="delete formErrors.order_invoice"
              />
            </v-col>

            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.order_configuration"
            >
              <SwitchField
                v-model="companyDetails.order_configuration.otp_verification"
                name="otp_verification"
                label="Otp Verification"
                :error-messages="formErrors.otp_verification"
                @change="delete formErrors.otp_verification"
              />
            </v-col>
            <v-col cols="12" md="3">
              <InputField
                label="SMS Integration API Key"
                v-model="
                  companyDetails.order_configuration.sms_integration_api_key
                "
                :error-messages="formErrors.sms_integration_api_key"
                @input="delete formErrors.sms_integration_api_key"
                :required="
                  companyDetails.order_configuration.otp_verification
                    ? true
                    : false
                "
              />
            </v-col>

            <v-col cols="12" class="d-flex align-center py-md-0">
              <span class="text-subtitle-2 font-weight-bold">
                Asset Configuration
              </span>
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.asset_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.asset_configuration.is_document_required
                "
                name="is_document_required"
                label="Is Document required"
                :error-messages="formErrors.is_document_required"
                @change="delete formErrors.is_document_required"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.asset_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.asset_configuration
                    .document_expiry_notification
                "
                name="document_expiry_notification"
                label="Document Expiry Notification"
                :error-messages="formErrors.document_expiry_notification"
                @change="delete formErrors.document_expiry_notification"
              />
            </v-col>
            <v-col cols="12" md="3" lg="3" class="d-flex align-center pb-md-5">
              <SwitchField
                v-model="
                  companyDetails.asset_configuration.has_telematics_device
                "
                name="has_telematics_device"
                label="Has Telematics Device"
                :error-messages="formErrors.has_telematics_device"
                @change="delete formErrors.has_telematics_device"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.asset_configuration.has_telematics_device"
            >
              <SelectField
                v-model="
                  companyDetails.asset_configuration.telematics_device_vendor
                "
                :rules="[
                  (val) => !!val || 'Telematics Device Vendor is required',
                ]"
                :itemsList="telematicsDeviceVendorList"
                name="telematics_device_vendor"
                label="Select Telematics Device Vendor *"
                :error-messages="formErrors.telematics_device_vendor"
                @change="delete formErrors.telematics_device_vendor"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center pb-md-5"
              v-if="companyDetails.asset_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.asset_configuration.driver_can_update_profile
                "
                name="driver_can_update_profile"
                label="Can Driver Update Profile"
                :error-messages="formErrors.driver_can_update_profile"
                @change="delete formErrors.driver_can_update_profile"
              />
            </v-col>
            <v-col cols="12" class="d-flex align-center py-md-0">
              <span class="text-subtitle-2 font-weight-bold">
                {{ tripModule.trip }} Configuration
              </span>
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="companyDetails.trip_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.trip_configuration.is_odometer_is_required
                "
                name="is_odometer_is_required"
                label="Is Odometer required"
                :error-messages="formErrors.is_odometer_is_required"
                @change="delete formErrors.is_odometer_is_required"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="companyDetails.trip_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.trip_configuration.is_helper_name_required
                "
                name="is_helper_name_required"
                label="Is Helper Name Required"
                :error-messages="formErrors.is_helper_name_required"
                @change="delete formErrors.is_helper_name_required"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="companyDetails.trip_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.trip_configuration
                    .complete_trip_without_attempting_order
                "
                name="complete_trip_without_attempting_order"
                :label="`Complete trip without attempting ${orderModule.order}`"
                :error-messages="
                  formErrors.complete_trip_without_attempting_order
                "
                @change="
                  delete formErrors.complete_trip_without_attempting_order
                "
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="companyDetails.trip_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.trip_configuration
                    .enable_warehouse_variable_processing_time
                "
                name="enable_warehouse_variable_processing_time"
                label="Enable warehouse Variable Processing Time"
                :error-messages="
                  formErrors?.trip_configuration
                    ?.enable_warehouse_variable_processing_time
                "
                @change="
                  delete formErrors.enable_warehouse_variable_processing_time
                "
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="
                companyDetails.trip_configuration &&
                companyDetails.trip_configuration
                  .enable_warehouse_variable_processing_time
              "
            >
              <InputField
                id="warehouse_variable_processing_time_per_100_kg"
                v-model="
                  companyDetails.trip_configuration
                    .warehouse_variable_processing_time_per_100_kg
                "
                label="Warehouse Variable Processing time / 100 Kg(In Minutes) "
                name="warehouse_variable_processing_time_per_100_kg"
                :rules="[
                  (val) =>
                    !val || val >= 0.1 || 'Value should be greater than 0.1',
                ]"
                :required="true"
                :error-messages="
                  formErrors?.trip_configuration
                    ?.warehouse_variable_processing_time_per_100_kg
                "
                @input="
                  delete formErrors.warehouse_variable_processing_time_per_100_kg
                "
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="companyDetails.trip_configuration"
            >
              <SwitchField
                v-model="
                  companyDetails.trip_configuration
                    .enable_customer_address_variable_processing_time
                "
                name="enable_customer_address_variable_processing_time"
                label="Enable Customer Address Variable Processing Time"
                :error-messages="
                  formErrors?.trip_configuration
                    ?.enable_customer_address_variable_processing_time
                "
                @change="
                  delete formErrors.enable_customer_address_variable_processing_time
                "
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
              lg="3"
              class="d-flex align-center"
              v-if="
                companyDetails.trip_configuration &&
                companyDetails?.trip_configuration
                  ?.enable_customer_address_variable_processing_time
              "
            >
              <InputField
                id="customer_address_variable_processing_time_per_100_kg"
                v-model="
                  companyDetails.trip_configuration
                    .customer_address_variable_processing_time_per_100_kg
                "
                label="Customer Address Variable Processing time / 100 Kg(In Minutes) "
                name="customer_address_variable_processing_time_per_100_kg"
                :rules="[
                  (val) =>
                    !val || val >= 0.1 || 'Value should be greater than 0.1',
                ]"
                :required="true"
                :error-messages="
                  formErrors?.trip_configuration
                    ?.customer_address_variable_processing_time_per_100_kg
                "
                @input="
                  delete formErrors.customer_address_variable_processing_time_per_100_kg
                "
              />
            </v-col>
          </v-row>
        </v-card>
      </v-form>
      <ThemePreview
        v-model="showThemePreview"
        :themeObject="selectedTheme"
        :profileDetail="{ ...companyDetails, logo: profileImage }"
      />
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import {
  generatedFormData,
  loadGoogleInstance,
  emailValidation,
} from "@/utils/functions.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import defaultImage from "@/assets/defaultLogo.png";
import ThemePreview from "@/components/GeneralComponents/ThemePreview.vue";
import {
  companyModule,
  subscriptionModule,
  orderModule,
  fleetModule,
  tripModule,
} from "@/utils/moduleMapping";
import { timezones } from "@/utils/fieldConstants.js";

export default {
  components: {
    BaseFormLayout,
    ThemePreview,
  },
  props: {
    editMode: {
      required: true,
    },
    isHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tripModule,
      orderModule,
      fleetModule,
      timezoneList: timezones,
      companyModule,
      emailValidation,
      subscriptionModule,
      defaultImage,

      fileInput: null,
      formCount: 0,
      isValid: true,
      companyDetails: {
        is_active: true,
        geofence_required: false,
        order_configuration: {
          has_items: false,
          allow_auto_address_update: false,
        },
        trip_configuration: {
          is_odometer_is_required: false,
          is_helper_name_required: false,
          complete_trip_without_attempting_order: false,
          enable_warehouse_variable_processing_time: false,
          enable_customer_address_variable_processing_time: false,
        },
        asset_configuration: {
          is_document_required: false,
          document_expiry_notification: false,
          driver_can_update_profile: false,
        },
        support_emails: [],
      },
      formErrors: {},
      accountManagerSelectList: [],
      telematicsDeviceVendorList: [],
      addressField: null,

      themeList: [],
      subscriptionList: [],

      profileImage: null,

      selectedTheme: {
        primary: "#1976D2",
        secondary: "#424242",
        accent: "#82B1FF",
        error: "#FF5252",
        info: "#2196F3",
        success: "#4CAF50",
        warning: "#FB8C00",
        heading: "#90a4ae",
        subHeading: "#cfd8dc",
      },
      supportEmail: null,
      showThemePreview: false,
    };
  },
  computed: {
    profileObject() {
      let obj = this.$helper.getUserDetailsLocalStorage();
      return obj;
    },
  },
  methods: {
    addEmail() {
      if (this.supportEmail && this.$refs.emails.validate()) {
        let v = this.supportEmail;
        if (this.companyDetails.support_emails.indexOf(v.trim()) == -1) {
          this.companyDetails.support_emails.push(v);
          this.supportEmail = null;
        } else {
          bus.$emit("showToastMessage", {
            message: "Email already exist!",
            color: "error",
          });
        }
      }
    },
    themeChanged(e) {
      this.selectedTheme = this.themeList.find((theme) => theme.id == e);
    },
    getThemePreview() {
      if (!this.selectedTheme) {
        bus.$emit("showToastMessage", {
          message: "Please Select theme first",
          color: "error",
        });
      } else {
        this.showThemePreview = true;
      }
    },
    loadAutoComplete() {
      const interval = setInterval(async () => {
        if (this.$refs.addressField && window.google && window.google.maps) {
          clearInterval(interval);

          let addressField = document.getElementById("addressField");

          this.addressField = new window.google.maps.places.Autocomplete(
            addressField
          );
          // this.addressField.setComponentRestrictions({
          //   country: [this.$globals.countryCode],
          // });

          addressField.setAttribute("placeholder", "");

          this.addressField.addListener("place_changed", () => {
            let place = this.addressField.getPlace();

            this.companyDetails = {
              ...this.companyDetails,
              address: place.formatted_address,
            };
          });
        } else {
          loadGoogleInstance();
        }
      }, 100);
    },
    updateProfile(image, isChangeable = false) {
      if (this.companyDetails.is_logo_updated || isChangeable) {
        this.companyDetails.is_logo_updated = true;
        if (image) {
          this.companyDetails.logo = image;
        } else {
          delete this.companyDetails.logo;
        }
      } else {
        this.companyDetails.is_logo_updated = false;
        delete this.companyDetails.logo;
      }
    },
    resetForm() {
      this.supportEmail = null;
      this.$refs.companyForm.reset();
      let obj = {
        is_active: true,
        geofence_required: false,
        order_configuration: {
          has_items: false,
          allow_auto_address_update: false,
        },
        trip_configuration: {
          is_odometer_is_required: false,
          is_helper_name_required: false,
          complete_trip_without_attempting_order: false,
          enable_warehouse_variable_processing_time: false,
          enable_customer_address_variable_processing_time: false,
        },
        asset_configuration: {
          is_document_required: false,
          document_expiry_notification: false,
          driver_can_update_profile: false,
        },
        support_emails: [],
      };
      this.companyDetails = { ...obj };
      if (this.profileObject && this.profileObject.role == "account_manager") {
        this.companyDetails = {
          account_manager: this.profileObject.id,
          ...obj,
        };
      }
    },
    getCompanyObject(id) {
      this.$api.company
        .getCompanyObject(id)
        .then((res) => {
          if (res?.data?.logo) {
            this.profileImage = res.data.logo;
            delete res.data.logo;
          }
          this.themeChanged(res.data.theme);
          this.companyDetails = res.data;
          this.emailList = [...this.companyDetails.support_emails];
        })
        .catch((err) => {
          console.error(err);
        });
    },
    getAccountSelectList() {
      bus.$emit("showLoader", true);
      this.$api.accountManager
        .getAccountSelectList({
          limit: "all",
        })
        .then((res) => {
          this.accountManagerSelectList = res.data.map((user) => {
            return {
              text: `${user.username} - ${user.name}`,
              value: user.id,
            };
          });
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getTelematicsDeviceVendorSelectList() {
      bus.$emit("showLoader", true);
      this.$api.telematicsVendor
        .getTelematicsDeviceVendorSelectList({
          limit: "all",
        })
        .then((res) => {
          this.telematicsDeviceVendorList = res.data.map(
            (telematics_vendor) => {
              return {
                text: `${telematics_vendor.name}`,
                value: telematics_vendor.id,
              };
            }
          );
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    submitForm() {
      if (!this.$refs.companyForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }
      let payload = { ...this.companyDetails };
      payload.order_configuration = JSON.stringify(payload.order_configuration);
      payload.trip_configuration = JSON.stringify(payload.trip_configuration);
      payload.asset_configuration = JSON.stringify(payload.asset_configuration);

      bus.$emit("showLoader", true);
      if (this.editMode) {
        if (payload && !payload.is_logo_updated) {
          delete payload.logo;
        }
        this.$api.company
          .updateCompanyObject({
            id: this.companyDetails.id,
            data: generatedFormData(payload),
          })
          .then(() => {
            bus.$emit("showToastMessage", {
              message: "Company information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.push({ name: "company" });
            bus.$emit("showLoader", false);
          })
          .catch((err) => {
            console.error(err);
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
            if (err.data?.logo) {
              bus.$emit("showToastMessage", {
                message:
                  "File type not supported. Only PNG, JPG, and JPEG allow",
                color: "error",
              });
            }
            if (!this.isHeader) {
              this.$emit("redirect", true, { company: res.data.id });
            } else {
              this.$router.push({ name: "company" });
            }
          });
      } else {
        this.$api.company
          .createCompanyObject(generatedFormData(payload))
          .then((res) => {
            bus.$emit("showToastMessage", {
              message: "Company created!",
              color: "success",
            });
            this.resetForm();
            bus.$emit("showLoader", false);
            if (!this.isHeader) {
              this.$emit("redirect", true, { company: res.data.id });
            } else {
              this.$router.push({ name: "company" });
            }
          })
          .catch((err) => {
            console.error(err);
            if (err.data?.logo) {
              bus.$emit("showToastMessage", {
                message:
                  "File type not supported. Only PNG, JPG, and JPEG allow",
                color: "error",
              });
            }
            this.formErrors = err.data;
            bus.$emit("showLoader", false);
          });
      }
    },
    async getThemeList() {
      bus.$emit("showLoader", true);
      await this.$api.theme
        .getThemeList({ limit: "all" })
        .then((res) => {
          this.themeList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    async getSubscriptionList() {
      bus.$emit("showLoader", true);
      await this.$api.subscription
        .getSubscriptionList({ limit: "all" })
        .then((res) => {
          this.subscriptionList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
  },
  async mounted() {
    // this.loadAutoComplete();
    await this.getThemeList();
    await this.getSubscriptionList();
    this.getTelematicsDeviceVendorSelectList();
    if (this.profileObject && this.profileObject.role != "account_manager") {
      this.getAccountSelectList();
    } else {
      this.companyDetails = {
        account_manager: this.profileObject.id,
        is_active: true,
        order_configuration: {
          has_items: false,
          allow_auto_address_update: false,
        },
        trip_configuration: {
          is_odometer_is_required: false,
          is_helper_name_required: false,
          complete_trip_without_attempting_order: false,
        },
        asset_configuration: {
          is_document_required: false,
          document_expiry_notification: false,
          driver_can_update_profile: false,
        },
        support_emails: [],
      };
    }
    if ("companyId" in this.$route.query) {
      await this.getCompanyObject(this.$route.query.companyId);
    }
    if ("id" in this.$route.params) {
      await this.getCompanyObject(this.$route.params.id);
    }
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
</style>
